import React from 'react';
import {Linking, Platform} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import {version} from '../../../package.json';

import {Button as FooterButton} from '../../components/Interface/Forms/Buttons/Button';
import {Icon} from '../../components/Interface/Typographies/Icon';
import {Header} from '../../components/Interface/Headers/Header';

import {useAuth} from '../../context/auth';
import {useGlobal} from '../../context/global';
import {
  Container,
  Content,
  UserWrapper,
  UserInfoWrapper,
  UserInfo,
  UserGreeting,
  UserName,
  Menu,
  MenuGroup,
  GroupTitle,
  GroupTitleRow,
  GroupTitleIcon,
  Item,
  LeftContent,
  ArrowIcon,
  Button,
  ItemTitle,
  Footer,
  Version,
  VersionButton,
  ProfileButton,
  ProfileInitials,
  ChipIconSolid,
} from './styles';
import {RFValue} from 'react-native-responsive-fontsize';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

type MenuItemConfig = {
  icon: string;
  community?: boolean;
  title: string;
  onPress: () => void;
  visible?: boolean;
};

type MenuGroupConfig = {
  title: string;
  icon?: string;
  items: MenuItemConfig[];
};

const isWeb = Platform.OS === 'web';

const WEB_VISIBLE_ITEMS = ['Minha conta', 'Reembolso', 'Termos e condições', 'Ajuda', 'Histórico de compras'];

function MenuItem({icon, community, title, onPress}: MenuItemConfig) {
  return (
    <Item>
      <Button onPress={onPress}>
        <LeftContent>
          <Icon name={icon} community={community} />
          <ItemTitle>{title}</ItemTitle>
        </LeftContent>
        <ArrowIcon name="chevron-right" />
      </Button>
    </Item>
  );
}

function MenuGroupSection({title, icon, items}: MenuGroupConfig) {
  return (
    <MenuGroup>
      {icon ? (
        <GroupTitleRow>
          <GroupTitleIcon name={icon} />
          <GroupTitle style={{marginBottom: 0}}>{title}</GroupTitle>
        </GroupTitleRow>
      ) : (
        <GroupTitle>{title}</GroupTitle>
      )}
      {items
        .filter(item => item.visible !== false)
        .map(item => (
          <MenuItem key={item.title} {...item} />
        ))}
    </MenuGroup>
  );
}

export function User() {
  const {user, isLogged, signOut, setShowSign, setShouldShowOTPModal} =
    useAuth();
  const {disabledFeatures} = useGlobal();
  const isDisabled = (key: string) => disabledFeatures.includes(key);

  const navigation = useNavigation();

  function handleSignOut() {
    signOut();
    navigation.navigate('Initial');
  }

  const maskedCpf = `XXX.XXX.XXX-${
    user.cpf.length > 2 ? user.cpf.substring(9, 11) : user.cpf
  }`;

  const loggedMenuGroups: MenuGroupConfig[] = [
    {
      title: 'Configurações',
      items: [
        {
          icon: 'user',
          title: 'Minha conta',
          onPress: () => navigation.navigate('Account'),
        },
        {
          icon: 'gear',
          title: 'Configurações do app',
          onPress: () => Linking.openSettings(),
        },
        {
          icon: 'cellphone-check',
          community: true,
          title: 'Validar SMS',
          onPress: () => setShouldShowOTPModal(true),
          visible: !user.phone_verified,
        },
      ],
    },
    ...(!isDisabled('indique')
      ? [
          {
            title: 'Indique e ganhe',
            items: [
              {
                icon: 'gift',
                title: 'Indique e ganhe',
                onPress: () => navigation.navigate('ReferAndWin'),
              },
            ],
          },
        ]
      : []),
    ...(!isDisabled('reembolso')
      ? [
          {
            title: 'Reembolso',
            items: [
              {
                icon: 'undo',
                title: 'Reembolso',
                onPress: () => navigation.navigate('Reembolsos'),
              },
            ],
          },
        ]
      : []),
    {
      title: 'Pagamentos',
      items: [
        {
          icon: 'credit-card-alt',
          title: 'Meus cartões',
          onPress: () => navigation.navigate('CardsWallet'),
        },
        {
          icon: 'clock-o',
          title: 'Histórico de compras',
          onPress: () => navigation.navigate('PaymentsHistory'),
        },
      ],
    },
    {
      title: 'BaratinhaZ',
      items: [
        {
          icon: 'file-text',
          title: 'Extrato das baratinhaZ',
          onPress: () => navigation.navigate('BaratinhasHistory'),
        },
      ],
    },
    {
      title: 'Suporte',
      items: [
        {
          icon: 'file',
          title: 'Termos e condições',
          onPress: () =>
            Linking.openURL(
              'https://barataocombustiveis.com.br/termos-e-condicoes',
            ),
        },
        {
          icon: 'question-circle',
          title: 'Ajuda',
          onPress: () => navigation.navigate('Help'),
        },
      ],
    },
  ];

  const guestMenuGroups: MenuGroupConfig[] = [
    {
      title: 'Configurações',
      items: [
        {
          icon: 'gear',
          title: 'Configurações do app',
          onPress: () => Linking.openSettings(),
        },
      ],
    },
    {
      title: 'Suporte',
      items: [
        {
          icon: 'file',
          title: 'Termos e condições',
          onPress: () =>
            Linking.openURL(
              'https://barataocombustiveis.com.br/termos-e-condicoes',
            ),
        },
        {
          icon: 'question-circle',
          title: 'Ajuda',
          onPress: () => navigation.navigate('Help'),
        },
      ],
    },
  ];

  const baseMenuGroups = isLogged ? loggedMenuGroups : guestMenuGroups;

  const webMenuGroups: MenuGroupConfig[] = [
    {
      title: 'Configurações',
      items: baseMenuGroups
        .flatMap(group => group.items)
        .filter(item => WEB_VISIBLE_ITEMS.includes(item.title)),
    },
  ];

  const menuGroups = isWeb ? webMenuGroups : baseMenuGroups;

  const {bottom} = useSafeAreaInsets();

  return (
    <Container>
      <Header icon="arrow-left" />
      <Content>
        <UserWrapper>
          <UserInfoWrapper>
            <ProfileButton>
              {user?.name !== 'Convidado' ? (
                <ProfileInitials>
                  {user.name

                    .slice(0, 2)

                    .toUpperCase()}
                </ProfileInitials>
              ) : (
                <ChipIconSolid name="user" />
              )}
            </ProfileButton>
            <UserInfo>
              <UserName>{user.name}</UserName>
              <UserGreeting>
                {isLogged ? `CPF: ${maskedCpf}` : 'Acesso limitado'}
              </UserGreeting>
            </UserInfo>
          </UserInfoWrapper>
        </UserWrapper>

        <Menu
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: RFValue(bottom + 20)}}>
          {menuGroups.map(group => (
            <MenuGroupSection key={group.title} {...group} />
          ))}

          {
            (isLogged ? (
              <>
                <VersionButton activeOpacity={1}>
                  <Version>Versão do app: {version}</Version>
                </VersionButton>
                <Footer>
                  <FooterButton
                    title="Sair do app"
                    variant="secondary"
                    enabled
                    onPress={handleSignOut}
                  />
                </Footer>
              </>
            ) : (
              <Footer>
                <VersionButton activeOpacity={1}>
                  <Version>Versão do app: {version}</Version>
                </VersionButton>
                <FooterButton
                  title="Entrar na conta"
                  variant="outline"
                  enabled
                  onPress={() => setShowSign(true)}
                />
              </Footer>
            ))}
        </Menu>
      </Content>
    </Container>
  );
}
